// StockAdjustmentsMobile.jsx — onglet « Ajustements de Stock » (mobile). CRUD complet.
// Mêmes endpoints et même payload que StockAdjustments (GestionStock.jsx:1751-1766).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal, theme } from "antd";
import {
  Summary, SearchBar, Chips, Segmented, EmptyState, Fab, Sheet, ActionRow, Note,
  Field, TextInput, TextAreaInput, SelectInput, Pill, Mono, Ico, fmt,
} from "../produits/productMobileKit";
import {
  fetchAdjustments, saveAdjustment, saveAdjustmentBatch, deleteAdjustment,
} from "./stockMobileApi";
import { PERM, fmtDateTime, fmtQty, fmtSigned } from "./stockMobileShared";
import { buildStockAdjustmentProductOptions } from "../../gestion-stock/stockAdjustmentProducts";
import {
  buildAdjustmentBatchPayload,
  buildAdjustmentDraftItems,
} from "../../gestion-stock/stockAdjustmentBatch";
import useStockFormResources, { stockQuantity } from "../../gestion-stock/useStockFormResources";
import StockAdjustmentValuation from "../../gestion-stock/StockAdjustmentValuation";
import { STOCK_ADJUSTMENT_REASONS, adjustmentReasonLabel } from "../../utils/stockAdjustmentValuation";
import "../../gestion-stock/stock-form-modals.css";

const PAGE = 20;
const ADD = "add";
const EMPTY_FORM = { product_id: null, adjustment_type: ADD, quantity: "", notes: "", reason_code: "inventory_difference", expected_valuation_event_id: null };

export default function StockAdjustmentsMobile({
  selectedWarehouse, selectedCompany, hasPermission, user,
  initialProductId, autoOpenCreate, initialItems = [], prefillRequestId,
  replenishmentContext, onReplenishmentCompleted, onReplenishmentCancelled,
}) {
  const { token } = theme.useToken();
  const valuationTheme = { display: "contents", "--sa-mobile-surface": token.colorBgElevated,
    "--sa-mobile-text": token.colorText, "--sa-mobile-label": token.colorTextSecondary,
    "--sa-mobile-info-bg": token.colorInfoBg, "--sa-mobile-info-text": token.colorInfoText };
  const can = {
    create: hasPermission(PERM.adjustCreate),
    edit: hasPermission(PERM.adjustEdit),
    delete: hasPermission(PERM.adjustDelete),
  };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const [sheet, setSheet] = useState(null);     // {mode:'form'|'actions', row}
  const [form, setForm] = useState(EMPTY_FORM);
  const [batchItems, setBatchItems] = useState([]);
  const [productToAdd, setProductToAdd] = useState("");
  const [saving, setSaving] = useState(false);
  const autoOpenedRef = useRef(false);
  const savingRef = useRef(false);
  const listRequestRef = useRef(0);
  const contextKey = `${user?.id}:${selectedCompany}:${selectedWarehouse}`;
  const contextRef = useRef(contextKey);
  contextRef.current = contextKey;
  const draftRef = useRef(null);
  draftRef.current = JSON.stringify({ form, batchItems });
  const resources = useStockFormResources({ kind: "adjustment", open: sheet?.mode === "form",
    userId: user?.id, companyId: selectedCompany, warehouseId: selectedWarehouse });
  const products = resources.products.data || [];

  const load = useCallback(
    async (p) => {
      const request = ++listRequestRef.current;
      const requestedContext = contextRef.current;
      if (!selectedWarehouse) { setRows([]); setTotal(0); return; }
      setLoading(true);
      try {
        const { rows: incoming, total: t } = await fetchAdjustments({
          warehouseId: selectedWarehouse, page: p, limit: PAGE,
        });
        if (request !== listRequestRef.current || requestedContext !== contextRef.current) return;
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(t);
      } catch {
        if (request === listRequestRef.current && requestedContext === contextRef.current) message.error("Impossible de charger les ajustements de stock");
      } finally {
        if (request === listRequestRef.current && requestedContext === contextRef.current) setLoading(false);
      }
    },
    [selectedWarehouse, selectedCompany, user?.id]
  );

  useEffect(() => { setPage(1); load(1); }, [load]);

  useEffect(() => {
    setSheet(null); setForm(EMPTY_FORM); setBatchItems([]); setProductToAdd("");
    setRows([]); setTotal(0); setSaving(false);
  }, [contextKey]);

  useEffect(() => {
    autoOpenedRef.current = false;
  }, [autoOpenCreate, initialProductId, prefillRequestId, selectedWarehouse]);

  // Deep-link /gestion-stock?tab=adjustments&product_id=<id>&create=1
  useEffect(() => {
    if (
      !autoOpenCreate ||
      autoOpenedRef.current ||
      !can.create ||
      !selectedWarehouse ||
      (initialProductId && products.length === 0)
    ) return;
    autoOpenedRef.current = true;
    const seeded = buildAdjustmentDraftItems(initialItems, products);
    if (seeded.length === 0 && initialProductId) {
      const product = products.find(
        (candidate) => Number(candidate.id) === Number(initialProductId)
      );
      if (product) {
        seeded.push({
          key: String(product.id),
          product_id: Number(product.id),
          product_name: product.name || product.nom_produit,
          current_stock: Number(product.current_stock) || 0,
          adjustment_type: ADD,
          quantity: null,
        });
      }
    }
    setBatchItems(seeded);
    setProductToAdd("");
    setForm({ ...EMPTY_FORM, product_id: null });
    setSheet({ mode: "form", row: null });
  }, [
    autoOpenCreate,
    initialItems,
    initialProductId,
    can.create,
    products,
    selectedWarehouse,
  ]);

  const addCount = useMemo(() => rows.filter((r) => r.adjustment_type === ADD).length, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "add" && r.adjustment_type !== ADD) return false;
      if (filter === "subtract" && r.adjustment_type === ADD) return false;
      if (q && ![r.product_name, r.notes, r.user_name].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, query, filter]);

  const productOptions = useMemo(
    () =>
      buildStockAdjustmentProductOptions(products).flatOptions.map((option) => ({
        value: option.value,
        label: `${option.selectedLabel} (stock : ${fmtQty(option.currentStock)})`,
      })),
    [products]
  );

  const openCreate = () => {
    if (!selectedWarehouse) return message.warning("Veuillez sélectionner un établissement");
    setForm(EMPTY_FORM);
    setBatchItems([]);
    setProductToAdd("");
    setSheet({ mode: "form", row: null });
  };
  const openEdit = (r) => {
    setBatchItems([]);
    setProductToAdd("");
    setForm({
      product_id: r.product_id ?? null,
      adjustment_type: r.adjustment_type || ADD,
      quantity: String(r.quantity ?? ""),
      notes: r.notes || "",
      reason_code: r.reason_code || "unspecified",
      expected_valuation_event_id: r.valuation_event_id || null,
    });
    setSheet({ mode: "form", row: r });
  };
  const openActions = (r) => {
    if (!can.edit && !can.delete) return;
    setSheet({ mode: "actions", row: r });
  };
  const close = () => {
    if (saving) return;
    const cancelledReplenishment =
      sheet?.mode === "form" && !sheet?.row && Boolean(replenishmentContext);
    setSheet(null);
    setBatchItems([]);
    setProductToAdd("");
    if (cancelledReplenishment) {
      onReplenishmentCancelled?.();
    }
  };

  const qtyNum = Number(String(form.quantity).replace(",", "."));
  const batchPayload = buildAdjustmentBatchPayload(batchItems);
  const batchValid =
    batchPayload.length > 0 &&
    batchPayload.every((item) => item.product_id && item.quantity);
  const valuesValid = sheet?.row
    ? Boolean(form.product_id) && Number.isFinite(qtyNum) && qtyNum > 0
    : batchValid;
  const verified = resources.products.status === "verified" && resources.warehouses.status === "verified";
  const formValid = valuesValid && verified;

  const addProductToBatch = (productId) => {
    const numericProductId = Number(productId);
    const product = products.find(
      (candidate) => Number(candidate.id) === numericProductId
    );
    if (!product) return;
    if (
      batchItems.some(
        (item) => Number(item.product_id) === numericProductId
      )
    ) {
      message.info("Ce produit est déjà présent dans l'ajustement.");
      setProductToAdd("");
      return;
    }
    setBatchItems((current) => [
      ...current,
      {
        key: String(numericProductId),
        product_id: numericProductId,
        product_name: product.name || product.nom_produit,
        current_stock: Number(product.current_stock) || 0,
        adjustment_type: ADD,
        quantity: null,
      },
    ]);
    setProductToAdd("");
  };

  const updateBatchItem = (productId, field, value) => {
    setBatchItems((current) =>
      current.map((item) =>
        Number(item.product_id) === Number(productId)
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const removeBatchItem = (productId) => {
    setBatchItems((current) =>
      current.filter(
        (item) => Number(item.product_id) !== Number(productId)
      )
    );
  };

  const submit = async () => {
    if (savingRef.current) return;
    if (!formValid) return message.warning("Ajoutez les produits et renseignez une quantité supérieure à 0 pour chaque ligne");
    savingRef.current = true;
    setSaving(true);
    const submittedContext = contextRef.current;
    const submittedDraft = draftRef.current;
    try {
      const fresh = await resources.refresh();
      if (submittedContext !== contextRef.current || submittedDraft !== draftRef.current) throw new Error("Le contexte ou la saisie a changé. Vérifiez l’ajustement avant de réessayer.");
      if (!fresh.warehouses.some(row => String(row.id) === String(selectedWarehouse))) throw new Error("L’établissement sélectionné n’est plus disponible.");
      for (const item of sheet.row ? [{ product_id: form.product_id }] : batchItems) {
        const product = fresh.products.find(row => String(row.id) === String(item.product_id));
        const stock = stockQuantity(product);
        if (!product || stock === null || (!sheet.row && item.adjustment_type === "subtract" && Number(item.quantity) > stock)) {
          throw new Error(`Vérifiez le stock de « ${item.product_name || product?.name || "ce produit"} ». La saisie est conservée.`);
        }
      }
      if (sheet.row) {
        await saveAdjustment(sheet.row.id, {
          company_id: selectedCompany,
          warehouse_id: selectedWarehouse,
          product_id: Number(form.product_id),
          quantity: qtyNum,
          adjustment_type: form.adjustment_type,
          notes: form.notes,
          reason_code: form.reason_code,
          expected_valuation_event_id: form.expected_valuation_event_id,
          created_by: user?.id,
        });
        message.success("Ajustement mis à jour avec succès");
      } else {
        await saveAdjustmentBatch({
          company_id: selectedCompany,
          warehouse_id: selectedWarehouse,
          request_id:
            prefillRequestId || `manual-stock-adjustment-${Date.now()}`,
          notes: form.notes,
          reason_code: form.reason_code,
          items: batchPayload,
        });
        message.success(
          `${batchPayload.length} ajustement${batchPayload.length > 1 ? "s" : ""} créé${batchPayload.length > 1 ? "s" : ""} avec succès`
        );
      }
      void resources.invalidate();
      if (submittedContext !== contextRef.current) return;
      setSheet(null);
      setBatchItems([]);
      setPage(1);
      load(1);
      if (!sheet.row && replenishmentContext) {
        onReplenishmentCompleted?.();
      }
    } catch (err) {
      message.error(
        err.response?.data?.error ||
        err.response?.data?.details ||
        err.message ||
        "Erreur lors de l'enregistrement de l'ajustement"
      );
    } finally { savingRef.current = false; if (submittedContext === contextRef.current) setSaving(false); }
  };

  const askDelete = (r) => {
    setSheet(null);
    Modal.confirm({
      title: "Supprimer cet ajustement ?",
      content: "Le mouvement de stock associé sera annulé.",
      okText: "Oui, supprimer", okType: "danger", cancelText: "Non", centered: true,
      onOk: async () => {
        try {
          await deleteAdjustment(r.id, selectedCompany, r.valuation_event_id ?? null);
          void resources.invalidate();
          message.success("Ajustement supprimé avec succès");
          setPage(1);
          load(1);
        } catch (err) {
          message.error(err.response?.data?.error || "Erreur lors de la suppression de l'ajustement");
        }
      },
    });
  };

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };

  if (!selectedWarehouse) {
    return (
      <div className="body">
        <EmptyState
          icon="warehouse" title="Aucun établissement sélectionné"
          text="Choisissez un établissement dans la barre de navigation pour ajuster son stock."
        />
      </div>
    );
  }

  return (
    <div className="stock-adjustments-mobile" style={valuationTheme}>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: "Ajustements", value: fmt(total) },
          { label: "Ajouts chargés", value: fmt(addCount) },
          { label: "Retraits chargés", value: fmt(rows.length - addCount) },
        ]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Rechercher un produit, une note…" />
        <Chips value={filter} onChange={setFilter} options={[
          { key: "all", label: "Tous" }, { key: "add", label: "Ajouts" }, { key: "subtract", label: "Retraits" },
        ]} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement des ajustements" text="Synchronisation en cours." />
        ) : !visible.length ? (
          <EmptyState
            icon="boxes"
            title={query || filter !== "all" ? "Aucun résultat" : "Aucun ajustement"}
            text={query || filter !== "all"
              ? "Aucun ajustement chargé ne correspond à votre recherche."
              : "Créez un ajustement pour corriger le stock de cet établissement."}
          />
        ) : (
          <div className="list">
            {visible.map((r) => {
              const isAdd = r.adjustment_type === ADD;
              const signed = (Number(r.quantity) || 0) * (isAdd ? 1 : -1);
              return (
                <button key={r.id} className="rcard" onClick={() => openActions(r)}>
                  <Mono name={r.product_name || "?"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{r.product_name}</div>
                    <div className="rcard__meta">
                      <Pill tone={isAdd ? "ok" : "danger"} label={isAdd ? "Ajout" : "Retrait"} />
                      <span className="rcard__sub">{fmtDateTime(r.created_at)}</span>
                    </div>
                    {r.user_name ? <div className="rcard__meta"><span className="rcard__sub">Par {r.user_name}</span></div> : null}
                    <div className="rcard__sub">{adjustmentReasonLabel(r.reason_code)}</div>
                    <div className="rcard__sub"><StockAdjustmentValuation adjustment={r} /></div>
                  </div>
                  <span className="rcard__price" style={{ color: isAdd ? "var(--green)" : "var(--red)" }}>
                    {fmtSigned(signed)}
                    <small> {r.unit_short_name || r.unit_name || ""}</small>
                  </span>
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label="Ajuster" /> : null}

      <Sheet
        open={sheet?.mode === "form"} onClose={close}
        title={sheet?.row ? "Modifier l'ajustement" : "Ajustement multi-produit"}
        subtitle={sheet?.row
          ? "Le stock de l'établissement sélectionné sera corrigé."
          : "L'achat reste recommandé ; l'ajustement corrige un écart d'inventaire."}
      >
        {resources.products.error || resources.warehouses.error ? <Note>
          Impossible d’actualiser les données. La saisie est conservée.
          <button className="btn btn--ghost" onClick={() => resources.refresh().catch(() => {})}>Réessayer</button>
        </Note> : !verified ? <Note>Vérification des produits et de l’établissement…</Note> : null}
        {sheet?.row ? (
          <>
            <Note>Valeur enregistrée : <StockAdjustmentValuation adjustment={sheet.row} /></Note>
            <Field label="Produit">
              {/* Le produit reste verrouillé en édition. */}
              <TextInput value={sheet.row.product_name || ""} readOnly />
            </Field>

            <Field label="Type d'ajustement">
              <Segmented
                value={form.adjustment_type}
                onChange={(v) => setForm((f) => ({ ...f, adjustment_type: v }))}
                options={[{ value: "add", label: "Ajout" }, { value: "subtract", label: "Retrait" }]}
              />
            </Field>

            <Field label="Quantité" hint="(valeur positive)">
              <TextInput
                value={form.quantity}
                onChange={(v) => setForm((f) => ({ ...f, quantity: v }))}
                placeholder="Ex : 12" inputMode="decimal" icon="boxes"
              />
            </Field>
          </>
        ) : (
          <>
            <Note>
              Enregistrez normalement l'approvisionnement dans Achats. Continuez
              ici uniquement s'il s'agit d'une correction d'inventaire.
            </Note>
            <Field label="Ajouter un produit">
            <SelectInput
              value={productToAdd}
              onChange={(value) => {
                setProductToAdd(value);
                if (value) addProductToBatch(value);
              }}
              options={productOptions}
              placeholder={products.length ? "Ajouter un produit" : "Aucun produit dans cet établissement"}
            />
            </Field>

            {!batchItems.length ? (
              <Note>Ajoutez au moins un produit à ajuster.</Note>
            ) : (
              batchItems.map((item) => (
                <div className="rcard" key={item.key} style={{ display: "block", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div>
                      <strong>{item.product_name}</strong>
                      <div className="rcard__sub">Stock actuel : {stockQuantity(products.find(row => String(row.id) === String(item.product_id))) ?? "Indisponible"}</div>
                    </div>
                    <button
                      className="iconbtn"
                      aria-label={`Retirer ${item.product_name}`}
                      onClick={() => removeBatchItem(item.product_id)}
                    >
                      <Ico name="trash-2" />
                    </button>
                  </div>
                  <Field label="Type">
                    <Segmented
                      value={item.adjustment_type}
                      onChange={(value) =>
                        updateBatchItem(item.product_id, "adjustment_type", value)
                      }
                      options={[{ value: "add", label: "Ajout" }, { value: "subtract", label: "Retrait" }]}
                    />
                  </Field>
                  <Field label="Quantité" hint="(valeur positive)">
                    <TextInput
                      value={item.quantity ?? ""}
                      onChange={(value) =>
                        updateBatchItem(item.product_id, "quantity", value)
                      }
                      placeholder="Ex : 12"
                      inputMode="decimal"
                      icon="boxes"
                    />
                  </Field>
                </div>
              ))
            )}
          </>
        )}

        <Field label="Motif"><SelectInput value={form.reason_code} options={STOCK_ADJUSTMENT_REASONS}
          onChange={value => setForm(current => ({ ...current, reason_code: value }))} /></Field>
        <Note>La valeur du stock sera conservée à l’enregistrement : lots FIFO pour un retrait, coût moyen des lots pour un ajout. Modifier uniquement le motif ou les notes conserve le coût enregistré.</Note>
        <Field label="Notes" hint="facultatif">
          <TextAreaInput
            value={form.notes}
            onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
            placeholder="Motif de l'ajustement…"
          />
        </Field>

        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={close} disabled={saving}>Annuler</button>
          <button className="btn btn--primary" onClick={submit} disabled={saving || !formValid}>
            <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
            {sheet?.row
              ? "Mettre à jour"
              : `Créer ${batchItems.length || ""} ajustement${batchItems.length > 1 ? "s" : ""}`}
          </button>
        </div>
      </Sheet>

      <Sheet open={sheet?.mode === "actions"} onClose={close} title={sheet?.row?.product_name}>
        {sheet?.row ? (
          <>
            <Note>{adjustmentReasonLabel(sheet.row.reason_code)} · <StockAdjustmentValuation adjustment={sheet.row} /></Note>
            {sheet.row.notes ? <Note>{sheet.row.notes}</Note> : null}
            {can.edit ? <ActionRow icon="pencil" label="Modifier l'ajustement" onClick={() => openEdit(sheet.row)} /> : null}
            {can.delete && sheet.row.is_deletable !== false ? (
              <ActionRow icon="trash-2" label="Supprimer l'ajustement" danger onClick={() => askDelete(sheet.row)} />
            ) : null}
          </>
        ) : null}
      </Sheet>
    </div>
  );
}
